import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';

import { fetchCustomers, ICustomer } from '../../store/slices/customers';
import { CustomerPrintable } from '../UI/UserInfo/CustomerPrintable';
import { authorizeClient } from '../../networking/client';
import { Buffer } from 'buffer';
import { Gender, selectGenders } from '../../store/slices/gender';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { IIdAndLabel } from '../UI/Inputs/ModalDropdown';
import { ICountry, selectCountries } from '../../store/slices/countries';
import { toast } from 'react-toastify';
import CustomDropdown from '../UI/Inputs/CustomDropdown';
import ModalDatePickerNewDesign from '../UI/Inputs/ModalDatePickerNewDesign';
import PrimaryButtonV2 from '../UI/Buttons/PrimaryButtonV2';
import PhoneInput from 'react-phone-number-input';
import CustomTextField from '../UI/Inputs/CustomTextField';
import POSITION = toast.POSITION;
import parsePhoneNumberFromString from 'libphonenumber-js';
import classes from './CustomerInfo.module.css';
import 'react-phone-number-input/style.css';
import { getBloodTypeOptions, getRelationshipStatusOptions } from '../../utils/translations';
import Modal from '../UI/Modals/Modal';
import { IDirtyState, selectDirtyState, setDirtyState } from '../../store/slices/auth';
import { useTranslation } from '../../hooks/useTranslations';

type CustomerInputType = {
  sex: string;
  birth_date: string;
  name: string;
  given_name: string;
};
type CustomerOtherInputType = {
  address_type: string;
  street: string;
  post_code: string;
  city: string;
  country: string | null;
  building_identifier: string;
};
type Props = {
  customer: ICustomer;
  showQRCode?: boolean;
  canUpdate?: boolean;
  onSelectCustomer?: () => void;
  onModalClose?: () => void;
};
const CustomerInfo: React.FC<Props> = ({
  customer: customer_,
  showQRCode = false,
  canUpdate,
  onSelectCustomer,
  onModalClose,
}) => {
  const { translations } = useTranslation();
  const dispatch = useAppDispatch();
  const genders: Gender[] = useAppSelector<Gender[]>(selectGenders);
  const countries: ICountry[] = useAppSelector<ICountry[]>(selectCountries);
  const [qrCode, setQRCode] = React.useState<string>();
  const [customer] = React.useState<ICustomer>(customer_);
  const [selectedGender, setSelectedGender] = useState<number>(customer.person_gender_id);
  const isDirtyModal = useAppSelector<IDirtyState>(selectDirtyState);
  const [email, setEmail] = useState<string>(customer_.person_email);
  const [language, setLanguage] = useState<string>('');
  const [initialLanguage, setInitialLanguage] = useState<string>('');
  const [isEmailUpdated, setIsEmailUpdated] = useState<boolean>(false);
  const [isEmailEditable, setIsEmailEditable] = useState<boolean>(!customer.person_email);
  const [phoneNumber, setPhoneNumber] = useState<string>(customer_.person_mobile_phone);
  const bloodTypeOptions = getBloodTypeOptions(translations);
  const relationshipStatusOptions = getRelationshipStatusOptions(translations);
  const [streetValidation, setStreetValidation] = useState<boolean>(true);
  const [houseNumberValidation, setHouseNumberValidation] = useState<boolean>(true);
  const [postalCodeValidation, setPostalCodeValidation] = useState<boolean>(true);
  const [cityValidation, setCityValidation] = useState<boolean>(true);
  const [countryIdValidation, setCountryIdValidation] = useState<boolean>(true);
  const [countryId, setCountryId] = useState<string>(
    customer_.countrycode ? customer_.countrycode.toString() : ''
  );
  const [hasOtherInputsUpdate, setHasOtherInputsUpdate] = React.useState<boolean>(false);

  const [customerInput, setCustomerInput] = React.useState<CustomerInputType>({
    sex: customer_.person_gender_id === 1 ? 'M' : 'F',
    birth_date: format(new Date(customer_.person_birthdate), 'yyyy-MM-dd'),
    name: customer_.person_lastname,
    given_name: customer_.person_firstname,
  });
  const [customerOtherInput, setCustomerOtherInput] = React.useState<CustomerOtherInputType>({
    address_type: customer_.address_type || 'L',
    street: customer_.street,
    building_identifier: customer_.house_number,
    city: customer_.city,
    post_code: customer_.postcode ? `${customer_.postcode}` : '',
    country: customer_.country_id ? customer_.country_id.toString() : null,
  });

  const [additionalInfoInput, setAdditionalInfoInput] = React.useState<any>({
    height: customer_?.person_details?.height || null,
    weight: customer_?.person_details?.weight || null,
    relation_status: customer_?.person_details?.relation_status || null,
    blood_type: customer_?.person_details?.blood_type || null,
    person_id: customer_?.person_external_id,
  });
  const [isLoadingUpdate, setIsLoadingUpdate] = React.useState<boolean>(false);

  const hasUpdate =
    customerInput.given_name !== customer.person_firstname ||
    customerInput.name !== customer.person_lastname ||
    customerInput.birth_date !== format(new Date(customer.person_birthdate), 'yyyy-MM-dd') ||
    phoneNumber !== customer.person_mobile_phone;

  const hasAdditionalInfoUpdate =
    (additionalInfoInput.height
      ? customer_?.person_details?.height !== (Number(additionalInfoInput.height) || null)
      : false) ||
    (additionalInfoInput.weight
      ? customer_?.person_details?.weight !== (Number(additionalInfoInput.weight) || null)
      : false) ||
    (additionalInfoInput.blood_type
      ? customer_?.person_details?.blood_type !== additionalInfoInput.blood_type
      : false) ||
    (additionalInfoInput.relation_status
      ? customer_?.person_details?.relation_status !== additionalInfoInput.relation_status
      : false);

  const isValidPhoneNumber = (phoneNumber: string) => {
    try {
      const phoneNumberInstance = parsePhoneNumberFromString(phoneNumber);
      return phoneNumberInstance && phoneNumberInstance.isValid();
    } catch (error) {
      console.error('Error parsing phone number:', error);
      return false;
    }
  };

  useEffect(() => {
    const fetchUserLanguage = async () => {
      try {
        const response = await authorizeClient.get(
          `/ident/person/${customer.person_external_id}/settings`
        );
        setLanguage(response.data.language);
        setInitialLanguage(response.data.language);
      } catch (error: any) {
        console.error('Error fetching language:', error);
        const errorCode = error?.response?.data?.error_code;
        toast.error(
          translations(errorCode ? `error.${errorCode}` : 'placeholder.something.went.wrong'),
          {
            position: POSITION.BOTTOM_RIGHT,
          }
        );
      }
    };

    fetchUserLanguage();
  }, [customer.person_external_id]);

  const validateFields = () => {
    let notEmptyRegex = /\S/;
    let numberRegex = /^\d+$/;

    return {
      isStreetEmpty: !notEmptyRegex.test(customerOtherInput.street || ''),
      isHouseNumberEmpty: !numberRegex.test(customerOtherInput.building_identifier || ''),
      isPostalCodeEmpty: !numberRegex.test(customerOtherInput.post_code || ''),
      isCityEmpty: !notEmptyRegex.test(customerOtherInput.city || ''),
      isCountryEmpty: !notEmptyRegex.test(customerOtherInput.country || ''),
    };
  };

  const handleAddressValidation = (inputValidation: boolean) => {
    if (!hasOtherInputsUpdate) {
      return false;
    } else {
      return inputValidation;
    }
  };
  useEffect(() => {
    const check =
      (customerOtherInput.street && customerOtherInput.street !== customer.street) ||
      (customerOtherInput.building_identifier &&
        customerOtherInput.building_identifier !== customer.house_number) ||
      (customerOtherInput.city && customerOtherInput.city !== customer.city) ||
      (customerOtherInput.post_code && customerOtherInput.post_code !== `${customer.postcode}`) ||
      (customerOtherInput.country && customerOtherInput.country !== customer.countrycode);
    setHasOtherInputsUpdate(Boolean(check));
  }, [customerOtherInput]);

  const handleCustomerUpdate = async () => {
    const { isStreetEmpty, isHouseNumberEmpty, isPostalCodeEmpty, isCityEmpty, isCountryEmpty } =
      validateFields();

    setStreetValidation(isStreetEmpty);
    setHouseNumberValidation(isHouseNumberEmpty);
    setPostalCodeValidation(isPostalCodeEmpty);
    setCityValidation(isCityEmpty);
    setCountryIdValidation(isCountryEmpty);

    if (!hasOtherInputsUpdate) {
      setStreetValidation(false);
      setHouseNumberValidation(false);
      setPostalCodeValidation(false);
      setCityValidation(false);
      setCountryIdValidation(false);
    }
    // if (
    //   (additionalInfoInput.height < 1 && additionalInfoInput.height !== '') ||
    //   (additionalInfoInput.weight < 1 && additionalInfoInput.weight !== '')
    // ) {
    //   toast.error(translations('error.valid_value'), {
    //     position: POSITION.BOTTOM_RIGHT,
    //   });
    //   return;
    // }
    setIsLoadingUpdate(true);
    try {
      if (!customer.person_email && email && !isEmailUpdated) {
        await authorizeClient.put(`/ident/person/${customer.person_external_id}/email_address`, {
          email_address: email,
          reset_password: false,
          is_primary: true,
        });
        setIsEmailUpdated(true);
        setIsEmailEditable(false);
      }
      const additionalInfosUpdated = {
        height: additionalInfoInput.height ? Number(additionalInfoInput.height) : null,
        weight: additionalInfoInput.weight ? Number(additionalInfoInput.weight) : null,
        relation_status: additionalInfoInput.relation_status || null,
        blood_type: additionalInfoInput.blood_type || null,
      };
      const additionalInfosData = Object.fromEntries(
        Object.entries(additionalInfosUpdated).filter(([_, value]) => value !== null)
      );
      if (customer?.person_details && hasAdditionalInfoUpdate) {
        await authorizeClient.put(
          `/ident/person/${additionalInfoInput.person_id}/details`,
          additionalInfosData
        );
      }
      if (!customer?.person_details && hasAdditionalInfoUpdate) {
        await authorizeClient.post(
          `/ident/person/${additionalInfoInput.person_id}/details`,
          additionalInfosData
        );
      }
      if (hasUpdate) {
        await authorizeClient.put(`/ident/person/${customer.person_external_id}`, customerInput);
      }
      if (hasOtherInputsUpdate) {
        const addressPayload = {
          address_type: customerOtherInput.address_type || 'L',
          street: customerOtherInput.street,
          building_identifier: customerOtherInput.building_identifier,
          city: customerOtherInput.city,
          post_code: customerOtherInput.post_code,
          country: customerOtherInput.country,
        };

        if (!customer.external_address_id) {
          await authorizeClient.post(
            `/ident/person/${customer.person_external_id}/address`,
            addressPayload
          );
        } else {
          await authorizeClient.put(
            `/ident/person/${customer.person_external_id}/address/${customer.external_address_id}`,
            addressPayload
          );
        }
      }
      if (phoneNumber && phoneNumber !== customer_.person_mobile_phone) {
        if (isValidPhoneNumber(phoneNumber)) {
          const phonePayload = {
            phone_number: phoneNumber,
            phone_number_class: 'P',
          };
          await authorizeClient.post(
            `/ident/person/${customer.person_external_id}/phone`,
            phonePayload
          );
        } else {
          toast.error(translations('error.invalid_phone_number'), {
            position: POSITION.BOTTOM_RIGHT,
          });
          return;
        }
      }
      if (language && language !== customer.language) {
        await authorizeClient.put(`/ident/person/${customer.person_external_id}/settings`, {
          language: language,
        });
      }
      await dispatch(fetchCustomers());
      toast.success(translations('customer.update.success'), { position: POSITION.BOTTOM_RIGHT });

      if (onModalClose) {
        onModalClose();
      }
    } catch (error: any) {
      const errorCode = error.response?.data?.error_code;
      toast.error(
        translations(errorCode ? `error.${errorCode}` : 'placeholder.something.went.wrong'),
        {
          position: POSITION.BOTTOM_RIGHT,
        }
      );
    } finally {
      setIsLoadingUpdate(false);
    }
    dispatch(setDirtyState({ isDirty: false, isOpen: false, component: '' }));
  };

  React.useEffect(() => {
    if (showQRCode) {
      const fetchQRCode = async () => {
        try {
          const qrCodeResponse = await authorizeClient.get(
            `/core/account/${customer.person_account_number}/qr.png`,
            {
              responseType: 'arraybuffer',
            }
          );
          const base64QR = Buffer.from(qrCodeResponse.data, 'binary').toString('base64');
          setQRCode(`data:image/png;base64,${base64QR}`);
        } catch (_) {
        } finally {
        }
      };
      if (customer.person_account_number) fetchQRCode();
    }
  }, [customer, showQRCode]);

  React.useEffect(() => {
    if (selectedGender === 1) {
      setCustomerInput({
        ...customerInput,
        sex: 'M',
      });
      return;
    }
    if (selectedGender === 2) {
      setCustomerInput({
        ...customerInput,
        sex: 'F',
      });
    }
  }, [selectedGender]);

  React.useEffect(() => {
    setCustomerOtherInput((prev) => ({
      ...prev,
      country: countryId.toString(),
    }));
  }, [countryId]);

  const isButtonDisabled = !(
    isLoadingUpdate ||
    hasUpdate ||
    hasAdditionalInfoUpdate ||
    hasOtherInputsUpdate ||
    email !== customer_.person_email ||
    language !== initialLanguage
  );

  useEffect(() => {
    const obj = { isDirty: !isButtonDisabled, isModalOpen: false, component: '' };
    dispatch(setDirtyState(obj));
  }, [isButtonDisabled]);

  const countryOptions = countries.map((country) => ({
    id: country.countrycode,
    label: country.country,
  }));

  return (
    <div className={classes.container}>
      <div className={classes.sectionHeader}>{translations('basics')}</div>
      <div className={classes.divider} />
      {showQRCode ? (
        <div className={classes.basicInfo}>
          <div className={classes.fieldsContainer}>
            <div className={classes.field}>
              <CustomTextField
                disabled={isLoadingUpdate || !canUpdate}
                label={translations('first_name') ?? ''}
                value={customerInput.given_name}
                onChange={(event) => {
                  canUpdate &&
                    setCustomerInput({ ...customerInput, given_name: event.target.value });
                }}
              />
            </div>
            <div className={classes.field}>
              <CustomTextField
                disabled={isLoadingUpdate || !canUpdate}
                label={translations('last_name') ?? ''}
                value={customerInput.name}
                onChange={(event) => {
                  canUpdate && setCustomerInput({ ...customerInput, name: event.target.value });
                }}
              />
            </div>
            <div className={classes.field}>
              <CustomDropdown
                disabled={isLoadingUpdate}
                label={translations('gender')}
                placeholder={translations('placeholder.choose_gender')}
                options={genders
                  .filter(
                    (g) =>
                      parseInt(g.gender_id?.toString()) === 1 ||
                      parseInt(g.gender_id?.toString()) === 2
                  )
                  .map((gender) => {
                    const result: IIdAndLabel = {
                      id: gender.gender_id,
                      label: gender.gender,
                    };
                    return result;
                  })}
                value={selectedGender}
                onChange={(event) => canUpdate && setSelectedGender(parseInt(event.target.value))}
              />
            </div>
            <div className={classes.field}>
              <ModalDatePickerNewDesign
                disabled={isLoadingUpdate || !canUpdate}
                // errorValidation={!fieldValidation.person_birthdate}
                placeholder={translations('date_of_birth').toString()}
                label={translations('date_of_birth') ?? ''}
                value={new Date(customerInput.birth_date)}
                onChange={(date: Date) => {
                  canUpdate &&
                    setCustomerInput({
                      ...customerInput,
                      birth_date: format(date, 'yyyy-MM-dd'),
                    });
                }}
              />
            </div>
          </div>
          <div className={classes.qrCodeContainer}>
            <img alt={'qrCode'} src={qrCode} />
            <CustomerPrintable customer={customer} qrCode={qrCode} />
          </div>
        </div>
      ) : (
        <div className={classes.fieldsContainer}>
          <div className={classes.field}>
            <CustomTextField
              disabled={isLoadingUpdate || !canUpdate}
              label={translations('first_name') ?? ''}
              value={customerInput.given_name}
              onChange={(event) => {
                canUpdate && setCustomerInput({ ...customerInput, given_name: event.target.value });
              }}

            // errorValidation={!fieldValidation.person_firstname}
            />
          </div>
          <div className={classes.field}>
            <CustomTextField
              disabled={isLoadingUpdate || !canUpdate}
              label={translations('last_name') ?? ''}
              value={customerInput.name}
              onChange={(event) => {
                canUpdate && setCustomerInput({ ...customerInput, name: event.target.value });
              }}
              required
            // errorValidation={!fieldValidation.person_lastname}
            />
          </div>
          <div className={classes.field}>
            <CustomDropdown
              disabled={isLoadingUpdate || !canUpdate}
              label={translations('gender')}
              placeholder={translations('select')}
              required
              options={genders
                .filter(
                  (g) =>
                    parseInt(g.gender_id?.toString()) === 1 ||
                    parseInt(g.gender_id?.toString()) === 2
                )
                .map((gender) => {
                  const result: IIdAndLabel = {
                    id: gender.gender_id,
                    label: gender.gender,
                  };
                  return result;
                })}
              value={selectedGender}
              onChange={(event) => canUpdate && setSelectedGender(parseInt(event.target.value))}
            />
          </div>
          <div className={classes.field}>
            <ModalDatePickerNewDesign
              disabled={isLoadingUpdate || !canUpdate}
              // errorValidation={!fieldValidation.person_birthdate}
              placeholder={translations('date_of_birth').toString()}
              label={translations('date_of_birth') ?? ''}
              value={new Date(customerInput.birth_date)}
              onChange={(date: Date) => {
                canUpdate &&
                  setCustomerInput({
                    ...customerInput,
                    birth_date: format(date, 'yyyy-MM-dd'),
                  });
              }}
            />
          </div>
        </div>
      )}
      <div className={classes.sectionHeader}>
        {translations('additional.informations')}
        <span className={classes.optionalLabel}> ({translations("label.optional")})</span>
      </div>
      <div className={classes.divider} />
      <div className={classes.fieldsContainer}>
        <div className={classes.field}>
          <CustomTextField
            disabled={isLoadingUpdate || !canUpdate}
            label={translations('height') ?? ''}
            value={additionalInfoInput.height}
            onChange={(event) => {
              const value = event.target.value;
              if (/^\d*$/.test(value)) {
                canUpdate && setAdditionalInfoInput({ ...additionalInfoInput, height: value });
              }
            }}
            errorValidation={
              additionalInfoInput.height
                ? (additionalInfoInput.height > 300 || additionalInfoInput.height < 1) &&
                additionalInfoInput.height !== ''
                : false
            }
            unit="cm"
          />
        </div>
        <div className={classes.field}>
          <CustomTextField
            disabled={isLoadingUpdate || !canUpdate}
            label={translations('weight') ?? ''}
            value={additionalInfoInput.weight}
            onChange={(event) => {
              const value = event.target.value;
              if (/^\d*$/.test(value)) {
                canUpdate && setAdditionalInfoInput({ ...additionalInfoInput, weight: value });
              }
            }}
            errorValidation={
              additionalInfoInput.weight
                ? (additionalInfoInput.weight > 700 || additionalInfoInput.weight < 1) &&
                additionalInfoInput.weight !== ''
                : false
            }
            unit="kg"
          />
        </div>
        <div className={classes.field}>
          <CustomDropdown
            disabled={isLoadingUpdate}
            label={translations('label_blood_types')}
            placeholder={translations('select')}
            options={bloodTypeOptions}
            value={additionalInfoInput.blood_type}
            onChange={(event) =>
              canUpdate &&
              setAdditionalInfoInput({
                ...additionalInfoInput,
                blood_type: event.target.value,
              })
            }
          />
        </div>
        <div className={classes.field}>
          <CustomDropdown
            disabled={isLoadingUpdate}
            label={translations('label_relationship_status')}
            placeholder={translations('select')}
            options={relationshipStatusOptions}
            value={additionalInfoInput.relation_status}
            onChange={(event) =>
              canUpdate &&
              setAdditionalInfoInput({
                ...additionalInfoInput,
                relation_status: event.target.value,
              })
            }
          />
        </div>
      </div>
      <div className={classes.sectionHeader}>
        {translations('others')}
        <span className={classes.optionalLabel}> ({translations("label.optional")})</span>
      </div>
      <div className={classes.divider} />
      <div className={classes.fieldsContainer}>
        <div className={classes.field}>
          <p className={classes.mobilePhoneLabel}>{translations('mobile_number')}</p>
          <PhoneInput
            style={{ width: 'auto', height: 46 }}
            flagComponent={(props) => <div>{props.countryName}</div>}
            international
            countryCallingCodeEditable={false}
            defaultCountry="DE"
            placeholder="Phone"
            value={phoneNumber}
            onChange={(val) => {
              setPhoneNumber(String(val));
            }}
          // labels={language === "de" ? de : undefined}
          />
        </div>
        <div className={classes.field}>
          <CustomTextField
            disabled={!isEmailEditable || isLoadingUpdate || !canUpdate}
            label={translations('email') ?? ''}
            value={email}
            onChange={(event) => {
              canUpdate && setEmail(event.target.value);
            }}
          />
        </div>
      </div>
      <div className={classes.fieldsContainer}>
        <div className={`${classes.field} `}>
          <CustomTextField
            disabled={isLoadingUpdate || !canUpdate}
            label={translations('street') ?? ''}
            value={customerOtherInput.street}
            onChange={(e) => {
              const value = e.target.value;
              canUpdate && setCustomerOtherInput({ ...customerOtherInput, street: value });
              const notEmptyRegex = /\S/;
              setStreetValidation(!notEmptyRegex.test(value));
            }}
            errorValidation={handleAddressValidation(streetValidation)}
          />
        </div>
        <div className={classes.field}>
          <CustomTextField
            disabled={isLoadingUpdate || !canUpdate}
            label={translations('house_nr') ?? ''}
            value={customerOtherInput.building_identifier}
            onChange={(e) => {
              const value = e.target.value;
              canUpdate &&
                setCustomerOtherInput({ ...customerOtherInput, building_identifier: value });
              let numberRegex = /^\d+$/;
              setHouseNumberValidation(!numberRegex.test(value));
            }}
            errorValidation={handleAddressValidation(houseNumberValidation)}
          />
        </div>
        <div className={classes.field}>
          <CustomTextField
            disabled={isLoadingUpdate || !canUpdate}
            label={translations('postal_code') ?? ''}
            value={customerOtherInput.post_code?.toString()}
            onChange={(e) => {
              const value = e.target.value;
              canUpdate && setCustomerOtherInput({ ...customerOtherInput, post_code: value });
              let numberRegex = /^\d+$/;
              setPostalCodeValidation(!numberRegex.test(value));
            }}
            maxWidth={'100%'}
            errorValidation={handleAddressValidation(postalCodeValidation)}
          />
        </div>
        <div className={classes.field}>
          <CustomTextField
            disabled={isLoadingUpdate || !canUpdate}
            label={translations('city') ?? ''}
            value={customerOtherInput.city}
            onChange={(e) => {
              const value = e.target.value;
              canUpdate && setCustomerOtherInput({ ...customerOtherInput, city: value });
              const notEmptyRegex = /\S/;
              setCityValidation(!notEmptyRegex.test(value));
            }}
            errorValidation={handleAddressValidation(cityValidation)}
          />
        </div>
        <div className={classes.field}>
          <CustomDropdown
            label={translations('country')}
            options={[{ id: '', label: translations("select") }, ...countryOptions]}
            maxWidth={'324px'}
            value={countryId}
            // onChange={(e) => setCountryId(e?.target?.value)}
            onChange={(e) => {
              const value = e.target.value;
              canUpdate && setCountryId(value);
              const notEmptyRegex = /\S/;
              setCountryIdValidation(!notEmptyRegex.test(value));
            }}
            errorValidation={handleAddressValidation(countryIdValidation)}
          />
        </div>
        <div className={classes.field}>
          <CustomDropdown
            label={translations('language')}
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            options={['de', 'en', 'fr','es'].map((lang) => {
              const result: IIdAndLabel = {
                id: lang,
                label: translations(lang),
              };
              return result;
            })}
            maxWidth={'453px'}
            placeholder=""
          />
        </div>
      </div>
      <div className={classes.fieldsContainer}>
        <div className={classes.field}>
          <CustomTextField
            disabled={isLoadingUpdate || !canUpdate}
            label={translations('nationality') ?? ''}
            value={customer.nationality_name ?? ''}
            onChange={() => { }}
          />
        </div>
        <div className={classes.field}>
          <CustomTextField
            disabled={isLoadingUpdate || !canUpdate}
            label={translations('passport_number') ?? ''}
            value={customer.person_identity_number}
            onChange={() => { }}
          />
        </div>
      </div>
      <div className={classes.buttonsContainer}>
        {canUpdate && (
          <PrimaryButtonV2
            disabled={isButtonDisabled}
            text={translations('update.customer')}
            buttonStyle={{
              marginRight: 20,
              minWidth: 270,
              height: 60,
            }}
            onClick={() => handleCustomerUpdate()}
          />
        )}
        {onSelectCustomer && (
          <PrimaryButtonV2
            text={translations('select.customer')}
            buttonStyle={{
              marginRight: 20,
              minWidth: 270,
              height: 60,
            }}
            onClick={onSelectCustomer}
          />
        )}
        {onModalClose && (
          <PrimaryButtonV2
            text={translations('cancel')}
            buttonStyle={{
              minWidth: 270,
              height: 60,
              borderWidth: 1,
            }}
            invertedColors
            onClick={onModalClose}
          />
        )}
      </div>
      {isDirtyModal.isModalOpen && (
        <Modal
          hideCloseButton
          isOpen={isDirtyModal.isModalOpen}
          onClose={() =>
            dispatch(setDirtyState({ isDirty: true, isModalOpen: false, component: '' }))
          }
          maxWidth={'500px'}
          containerPadding={'70px'}
        >
          <div className={classes.text}>{translations('label.save_your')}</div>
          <div className={classes.ModalButtonsContainer}>
            <div
              className={`${classes.button} ${classes.assingButton}`}
              onClick={() => handleCustomerUpdate()}
            >
              {translations('save')}
            </div>
            <div
              className={`${classes.button} ${classes.cancelButton}`}
              onClick={() =>
                dispatch(setDirtyState({ isDirty: true, isModalOpen: false, component: '' }))
              }
            >
              {translations('button.cancel')}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export { CustomerInfo };
